import { useState, useEffect, useCallback } from "react";
import { useLocalePath } from "../../hooks/useLocalePath";

const COOKIE_NAME = "pb_cookie_consent";
const COOKIE_DAYS = 365;

function getCookie(name) {
  const v = document.cookie.match("(^|;)\\s*" + name + "\\s*=\\s*([^;]+)");
  return v ? v.pop() : null;
}

function setCookie(name, value, days) {
  const d = new Date();
  d.setTime(d.getTime() + days * 86400000);
  document.cookie =
    name +
    "=" +
    encodeURIComponent(value) +
    ";expires=" +
    d.toUTCString() +
    ";path=/;SameSite=Lax;Secure";
}

/**
 * CookieConsent — GDPR cookie consent banner.
 * Renders only on public pages (inside MainContainer).
 * Stores choice in cookie for 365 days.
 */
export default function CookieConsent() {
  const lp = useLocalePath();
  const [visible, setVisible] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(false);

  // On mount: check existing consent
  useEffect(() => {
    const raw = getCookie(COOKIE_NAME);
    if (raw) {
      try {
        JSON.parse(decodeURIComponent(raw));
        // Consent already stored — nothing to show
      } catch {
        // Corrupted cookie — show banner
        setVisible(true);
      }
    } else {
      // No consent yet — show banner after 1.5s
      const t = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(t);
    }
  }, []);

  const saveConsent = useCallback(
    (analyticsOn, marketingOn) => {
      const consent = {
        essential: true,
        analytics: analyticsOn,
        marketing: marketingOn,
        timestamp: new Date().toISOString(),
        version: "2",
      };
      setCookie(COOKIE_NAME, JSON.stringify(consent), COOKIE_DAYS);

      // Google Consent Mode v2 — immediately push update so GA4/GTM can unlock
      // tracking without waiting for page reload. Per Google docs this should
      // happen on the same page where the user clicked (no transition).
      try {
        if (typeof window !== "undefined" && typeof window.gtag === "function") {
          window.gtag("consent", "update", {
            ad_storage: marketingOn ? "granted" : "denied",
            ad_user_data: marketingOn ? "granted" : "denied",
            ad_personalization: marketingOn ? "granted" : "denied",
            analytics_storage: analyticsOn ? "granted" : "denied",
          });
        }
      } catch { /* no-op */ }

      // Notify AnalyticsLoader and any other listeners of the consent change,
      // so non-Google trackers (FB Pixel, Hotjar, etc.) can inject right away.
      try {
        window.dispatchEvent(
          new CustomEvent("pb:consent:changed", { detail: consent })
        );
      } catch { /* no-op */ }

      setVisible(false);
    },
    []
  );

  const handleAcceptAll = useCallback(() => {
    if (settingsOpen) {
      saveConsent(analytics, marketing);
    } else {
      saveConsent(true, true);
    }
  }, [settingsOpen, analytics, marketing, saveConsent]);

  const handleDecline = useCallback(() => {
    saveConsent(false, false);
  }, [saveConsent]);

  if (!visible) return null;

  return (
    <>
      {/* Overlay */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.3)",
          zIndex: 99998,
        }}
      />

      {/* Banner */}
      <div
        role="dialog"
        aria-label="Cookie consent"
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 99999,
          fontFamily: "system-ui, -apple-system, sans-serif",
        }}
      >
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            padding: "20px 24px",
            background: "#0f172a",
            borderTop: "1px solid #334155",
            boxShadow: "0 -4px 30px rgba(0,0,0,0.3)",
          }}
        >
          {/* Main row */}
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 20,
              flexWrap: "wrap",
            }}
          >
            {/* Text */}
            <div style={{ flex: 1, minWidth: 280 }}>
              <h3
                style={{
                  color: "#f1f5f9",
                  fontSize: 16,
                  fontWeight: 600,
                  margin: "0 0 6px",
                }}
              >
                🍪 We use cookies
              </h3>
              <p
                style={{
                  color: "#94a3b8",
                  fontSize: 13,
                  lineHeight: 1.5,
                  margin: 0,
                }}
              >
                We use cookies and similar technologies to improve your
                experience, analyze traffic, and personalize content. You can
                choose which cookies to allow. Read our{" "}
                <a
                  href={lp("/privacy")}
                  style={{ color: "#3b82f6", textDecoration: "underline" }}
                >
                  Privacy Policy
                </a>{" "}
                and{" "}
                <a
                  href={lp("/terms")}
                  style={{ color: "#3b82f6", textDecoration: "underline" }}
                >
                  Terms of Service
                </a>
                .
              </p>
              <button
                onClick={() => setSettingsOpen((p) => !p)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#64748b",
                  fontSize: 13,
                  cursor: "pointer",
                  textDecoration: "underline",
                  padding: "8px 0 0",
                }}
              >
                ⚙ Cookie settings
              </button>

              {/* Settings panel */}
              {settingsOpen && (
                <div
                  style={{
                    marginTop: 16,
                    paddingTop: 16,
                    borderTop: "1px solid #1e293b",
                  }}
                >
                  {/* Essential */}
                  <ToggleRow
                    label="Essential cookies"
                    desc="Required for the website to function. Cannot be disabled."
                    checked
                    disabled
                  />
                  {/* Analytics */}
                  <ToggleRow
                    label="Analytics cookies"
                    desc="Help us understand how visitors interact with our website (Google Analytics)."
                    checked={analytics}
                    onChange={(e) => setAnalytics(e.target.checked)}
                  />
                  {/* Marketing */}
                  <ToggleRow
                    label="Marketing cookies"
                    desc="Used to deliver relevant ads and track marketing campaigns."
                    checked={marketing}
                    onChange={(e) => setMarketing(e.target.checked)}
                  />
                </div>
              )}
            </div>

            {/* Buttons */}
            <div
              style={{
                display: "flex",
                gap: 10,
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <button onClick={handleDecline} style={btnDecline}>
                Decline
              </button>
              <button onClick={handleAcceptAll} style={btnAccept}>
                {settingsOpen ? "Save Preferences" : "Accept All"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/* Toggle row sub-component */
function ToggleRow({ label, desc, checked, disabled, onChange }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "10px 0",
        borderBottom: "1px solid #1e293b",
      }}
    >
      <div>
        <div style={{ color: "#f1f5f9", fontSize: 14 }}>{label}</div>
        <div style={{ color: "#64748b", fontSize: 12, marginTop: 2 }}>
          {desc}
        </div>
      </div>
      <label style={{ position: "relative", width: 44, height: 24, flexShrink: 0 }}>
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={onChange}
          style={{ opacity: 0, width: 0, height: 0, position: "absolute" }}
        />
        <span
          style={{
            position: "absolute",
            cursor: disabled ? "default" : "pointer",
            inset: 0,
            background: checked ? "#2563eb" : disabled ? "#1e293b" : "#475569",
            borderRadius: 24,
            transition: "0.3s",
          }}
        />
        <span
          style={{
            position: "absolute",
            height: 18,
            width: 18,
            left: checked ? 23 : 3,
            bottom: 3,
            background: "#fff",
            borderRadius: "50%",
            transition: "0.3s",
          }}
        />
      </label>
    </div>
  );
}

const btnAccept = {
  padding: "10px 22px",
  borderRadius: 10,
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
  border: "none",
  background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
  color: "#fff",
  whiteSpace: "nowrap",
};

const btnDecline = {
  padding: "10px 22px",
  borderRadius: 10,
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
  background: "transparent",
  color: "#94a3b8",
  border: "1px solid #475569",
  whiteSpace: "nowrap",
};
